import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { toast } from 'react-toastify';

import { apiErrorMessage } from '@/api/apiErrorMessage.util.ts';
import { HTTP_STATUS } from '@/api/ErrorCode.model.ts';
import { CONFIG } from '@/config/config.ts';
import { ToastConfig } from '@/config/ToastConfig.util.ts';
import { captureAppException } from '@/monitoring/sentry.util.ts';
import { LocalStorageKey, localStorageUtil } from '@/utils/localStorage/localStorage.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import {
  isOfferLockedError,
  refreshOfferQueries,
} from '@/views/processes/offer/util/offerLock.util.ts';

export const useGetQueryClient = (): QueryClient => {
  const { t } = useTranslationWithPrefix('utils.toasts.fetch');

  return useMemo(() => {
    const handleMinorError = (detail?: string) => {
      toast(detail ?? t('error'), ToastConfig.error);
    };

    const handleOperationSuccess = () => {
      toast(t('success'), ToastConfig.success);
    };

    const queryClient: QueryClient = new QueryClient({
      defaultOptions: {
        mutations: {
          retry: false,
        },
        queries: {
          refetchOnWindowFocus: false,
          retry: false,
        },
      },
      mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
          if (handle401(error, t)) {
            return;
          }

          captureAppException(error, { contextName: 'react-query-mutation' });

          if (isOfferLockedError(error)) {
            void refreshOfferQueries(queryClient);
          }

          if (mutation?.options?.meta?.disableErrorAlert) {
            return;
          }
          handleError(error, handleMinorError);
        },
        onSuccess: (_data, _variables, _context, mutation) => {
          if (mutation?.options?.meta?.disableSuccessAlert) {
            return;
          }
          handleOperationSuccess();
        },
      }),
      queryCache: new QueryCache({
        onError: (error: unknown, query) => {
          if (handle401(error, t)) {
            return;
          }

          captureAppException(error, { contextName: 'react-query-query' });

          if (query?.options?.meta?.disableErrorAlert) {
            return;
          }
          handleError(error, handleMinorError);
        },
        onSuccess: (_data, query) => {
          if (query?.options?.meta?.enableSuccessAlert) {
            handleOperationSuccess();
          }
        },
      }),
    });
    return queryClient;
  }, [t]);
};

const handleError = (error: unknown, handleMinorError: (error?: string) => void) => {
  if (!error) {
    handleMinorError('Unknown Error');
    return;
  }
  if (typeof error === 'string') {
    handleMinorError(error);
    return;
  }

  handleMinorError(apiErrorMessage(error));
};

export const LOGIN_REDIRECT_URL = CONFIG.HOST + '/' + import.meta.env.VITE_LOGIN_REDIRECT_URL;

const handle401 = (error: unknown, t: TFunction): boolean => {
  if ((error as AxiosError)?.response?.status === HTTP_STATUS.UNAUTHORIZED) {
    console.debug('401 redirect');
    t('401');
    localStorageUtil.setItem({ key: LocalStorageKey.LAST_VISITED_PAGE, value: location.href });
    globalThis.location.href = LOGIN_REDIRECT_URL;
    return true;
  }
  return false;
};
